// Asset manifest — a single JSON file at public/assets/manifest.json that maps
// logical keys (npc id, mob id, item template id, room id) to image file paths
// under public/assets/. The client loads it once at boot via HOO-53 and every
// panel that shows an image goes through assetUrl(kind, key).
//
// Design notes:
//  - Missing entries fall through to the SVG placeholder generator (HOO-51).
//  - Manifests are versioned so we can re-author without breaking caches.
//  - Keys are numeric strings or stable room ids; no runtime dependence on DB.

export type AssetKind = "scene" | "npc" | "mob" | "item" | "icon";

export interface AssetManifest {
  version: number;
  generated_at: string;
  /** URL prefix everyone prepends (defaults to "/assets/"). */
  base_url: string;
  /** kind -> key -> relative path (under base_url). */
  entries: {
    scene: Record<string, string>;   // key: "<zone>/<room>" or "atmosphere:<preset>"
    npc:   Record<string, string>;   // key: npc template id (stringified)
    mob:   Record<string, string>;   // key: mob template id (stringified)
    item:  Record<string, string>;   // key: item template id (stringified)
    icon:  Record<string, string>;   // key: item icon handle (atavism icon column)
  };
}

export const MANIFEST_PATH = "/assets/manifest.json";

export function emptyManifest(): AssetManifest {
  return {
    version: 1,
    generated_at: new Date().toISOString(),
    base_url: "/assets/",
    entries: { scene: {}, npc: {}, mob: {}, item: {}, icon: {} },
  };
}

/**
 * Resolve an asset URL. Returns null when the manifest has no entry — callers
 * should fall back to the placeholder generator with a deterministic seed.
 */
export function lookupAsset(manifest: AssetManifest, kind: AssetKind, key: string | number): string | null {
  const bucket = manifest.entries[kind];
  if (!bucket) return null;
  const hit = bucket[String(key)];
  if (!hit) return null;
  return hit.startsWith("/") || hit.startsWith("http") ? hit : manifest.base_url + hit;
}
